import { create } from 'zustand';
import { getAuthAxios } from './index';

export const useAdminStore = create((set) => ({
  dashboardStats: null,
  companies: [],
  users: [],
  messages: [],
  settings: null,
  announcements: [],
  packages: [],
  isLoading: false,
  error: null,

  fetchDashboard: async () => {
    set({ isLoading: true });
    try {
      const api = getAuthAxios();
      const res = await api.get('/admin/dashboard');
      set({ dashboardStats: res.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Veri yüklenemedi', isLoading: false });
    }
  },

  fetchCompanies: async () => {
    set({ isLoading: true });
    try {
      const api = getAuthAxios();
      const res = await api.get('/admin/companies');
      set({ companies: res.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Firmalar yüklenemedi', isLoading: false });
    }
  },

  suspendCompany: async (id, reason) => {
    try {
      const api = getAuthAxios();
      await api.post(`/admin/companies/${id}/suspend`, null, { params: { reason } });
      set((s) => ({
        companies: s.companies.map((c) =>
          c.id === id ? { ...c, is_suspended: true } : c
        ),
      }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  activateCompany: async (id) => {
    try {
      const api = getAuthAxios();
      await api.post(`/admin/companies/${id}/activate`);
      set((s) => ({
        companies: s.companies.map((c) =>
          c.id === id ? { ...c, is_suspended: false, is_active: true } : c
        ),
      }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  updateCompanyLimits: async (id, limits) => {
    try {
      const api = getAuthAxios();
      await api.put(`/admin/companies/${id}/limits`, limits);
      set((s) => ({
        companies: s.companies.map((c) =>
          c.id === id ? { ...c, limits } : c
        ),
      }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  fetchUsers: async () => {
    set({ isLoading: true });
    try {
      const api = getAuthAxios();
      const res = await api.get('/admin/users');
      set({ users: res.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Kullanıcılar yüklenemedi', isLoading: false });
    }
  },

  deleteCompany: async (companyId) => {
    try {
      const api = getAuthAxios();
      await api.delete(`/admin/companies/${companyId}`);
      set((s) => ({ companies: s.companies.filter((c) => c.id !== companyId) }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  deleteUser: async (userId) => {
    try {
      const api = getAuthAxios();
      await api.delete(`/admin/users/${userId}`);
      set((s) => ({ users: s.users.filter((u) => u.id !== userId) }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  resetUserPassword: async (userId, newPassword) => {
    try {
      const api = getAuthAxios();
      await api.put(`/admin/users/${userId}/reset-password`, { new_password: newPassword });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  fetchMessages: async (limit = 100) => {
    set({ isLoading: true });
    try {
      const api = getAuthAxios();
      const res = await api.get(`/admin/messages?limit=${limit}`);
      set({ messages: res.data, isLoading: false });
    } catch (error) {
      set({ error: error.response?.data?.detail || 'Mesajlar yüklenemedi', isLoading: false });
    }
  },

  deleteMessage: async (id) => {
    try {
      const api = getAuthAxios();
      await api.delete(`/admin/messages/${id}`);
      set((s) => ({ messages: s.messages.filter((m) => m.id !== id) }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  deleteAllMessages: async () => {
    try {
      const api = getAuthAxios();
      const res = await api.delete('/admin/messages');
      set({ messages: [] });
      return { success: true, message: res.data.message };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  fetchSettings: async () => {
    try {
      const api = getAuthAxios();
      const res = await api.get('/admin/settings');
      set({ settings: res.data });
    } catch (error) {
      set({ error: error.response?.data?.detail });
    }
  },

  updateSettings: async (data) => {
    try {
      const api = getAuthAxios();
      const res = await api.put('/admin/settings', data);
      set({ settings: res.data });
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  fetchAnnouncements: async () => {
    try {
      const api = getAuthAxios();
      const res = await api.get('/admin/announcements');
      set({ announcements: res.data });
    } catch (error) {
      set({ error: error.response?.data?.detail });
    }
  },

  createAnnouncement: async (data) => {
    try {
      const api = getAuthAxios();
      const res = await api.post('/admin/announcements', data);
      set((s) => ({ announcements: [res.data, ...s.announcements] }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  deleteAnnouncement: async (id) => {
    try {
      const api = getAuthAxios();
      await api.delete(`/admin/announcements/${id}`);
      set((s) => ({ announcements: s.announcements.filter((a) => a.id !== id) }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  updateSubscription: async (companyId, data) => {
    try {
      const api = getAuthAxios();
      await api.put(`/admin/companies/${companyId}/subscription`, data);
      set((s) => ({
        companies: s.companies.map((c) =>
          c.id === companyId ? { ...c, ...data } : c
        ),
      }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  startSubscription: async (companyId, data) => {
    try {
      const api = getAuthAxios();
      const res = await api.post(`/admin/companies/${companyId}/start-subscription`, data);
      set((s) => ({
        companies: s.companies.map((c) =>
          c.id === companyId ? { ...c, subscription_status: 'active', subscription_price: data.price } : c
        ),
      }));
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  fetchPackages: async () => {
    try {
      const api = getAuthAxios();
      const res = await api.get('/admin/packages');
      set({ packages: res.data });
    } catch (error) {
      set({ error: error.response?.data?.detail });
    }
  },

  updatePackage: async (packageId, data) => {
    try {
      const api = getAuthAxios();
      const res = await api.put(`/admin/packages/${packageId}`, data);
      set((s) => ({
        packages: s.packages.map((p) => p.id === packageId ? res.data : p),
      }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  assignPackage: async (companyId, data) => {
    try {
      const api = getAuthAxios();
      const res = await api.post(`/admin/companies/${companyId}/assign-package`, data);
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  // Payment Methods
  paymentMethods: [],

  fetchPaymentMethods: async () => {
    try {
      const api = getAuthAxios();
      const res = await api.get('/admin/payment-methods');
      set({ paymentMethods: res.data });
    } catch (error) {
      set({ error: error.response?.data?.detail });
    }
  },

  createPaymentMethod: async (data) => {
    try {
      const api = getAuthAxios();
      const res = await api.post('/admin/payment-methods', data);
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  updatePaymentMethod: async (methodId, data) => {
    try {
      const api = getAuthAxios();
      const res = await api.put(`/admin/payment-methods/${methodId}`, data);
      set((s) => ({
        paymentMethods: s.paymentMethods.map((m) => m.id === methodId ? res.data : m),
      }));
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  deletePaymentMethod: async (methodId) => {
    try {
      const api = getAuthAxios();
      await api.delete(`/admin/payment-methods/${methodId}`);
      set((s) => ({ paymentMethods: s.paymentMethods.filter((m) => m.id !== methodId) }));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  approvePayment: async (companyId) => {
    try {
      const api = getAuthAxios();
      const res = await api.post(`/admin/companies/${companyId}/approve-payment`);
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },

  impersonateCompany: async (companyId) => {
    try {
      const api = getAuthAxios();
      const res = await api.post(`/admin/companies/${companyId}/impersonate`);
      return { success: true, data: res.data };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail };
    }
  },
}));
